import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { usePymbleApi } from "./usePymbleApi";
import { splitBySubscription } from "../components/tabs/memberSections";

const GROUPS_ENDPOINT = "/group";
const GROUPMEMBERS_ENDPOINT = "/group/members";

export interface GroupFilter {
  search?: string;
  active_only?: number;
  type?: number[];
}

interface MemberRecord {
  _id: string;
  id?: string;
  MemberName?: string;
  Permission?: { Admin?: boolean };
  Subscribed?: boolean;
  admin?: boolean;
  [key: string]: unknown;
}

// Only sort by name at the moment
const sortGroupMembers = (data: MemberRecord[]) => {
  return [...data].sort(function (a, b) {
    try {
      if (a.MemberName && b.MemberName) {
        const nameA = a.MemberName.toUpperCase();
        const nameB = b.MemberName.toUpperCase();
        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
      }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_e) {
      /* squash */
    }
    return 0;
  });
};

const decorateForGrid = (memberdata: MemberRecord[]) =>
  memberdata.map((m) => {
    m.admin = m?.Permission?.Admin;
    m.id = m._id;
    return m;
  });

/**
 * Hook to fetch api data from groups api
 */
export function useGroupMembers(
  group_id: string,
  show_expired: boolean = false
) {
  const { callApi, isAuthenticated } = usePymbleApi();
  const queryClient = useQueryClient();

  const [groupMembersFilter, setGroupMembersFilter] = useState<GroupFilter>({
    search: "",
    active_only: 1,
    type: [],
  });
  const [groupSubscribersFilter, setGroupSubscribersFilter] =
    useState<GroupFilter>({
      search: "",
      active_only: 1,
      type: [],
    });

  const { data: rawData, isLoading } = useQuery({
    queryKey: ["groupMembers", group_id, show_expired],
    queryFn: async () => {
      if (!group_id) return { members: [], subscribers: [] };
      const res = await callApi(
        "GET",
        `${GROUPS_ENDPOINT}/${group_id}/members` +
          (show_expired ? "?show_expired=1" : "")
      );
      if (res) {
        const split = splitBySubscription(res as MemberRecord[]);
        return {
          members: sortGroupMembers(decorateForGrid(split.members)),
          subscribers: sortGroupMembers(decorateForGrid(split.subscribers)),
        };
      }
      return { members: [], subscribers: [] };
    },
    enabled: isAuthenticated && !!group_id,
    staleTime: 1000 * 60 * 5, // 5 min
  });

  const groupMembers = useMemo(() => rawData?.members ?? [], [rawData]);
  const groupSubscribers = useMemo(() => rawData?.subscribers ?? [], [rawData]);

  // Client-side filtering for members
  const groupMembersFiltered = useMemo(() => {
    const search = groupMembersFilter.search;
    if (search) {
      return groupMembers.filter(
        (m) =>
          m.MemberName &&
          m.MemberName.toLowerCase().includes(search.toLowerCase())
      );
    }
    return groupMembers;
  }, [groupMembers, groupMembersFilter.search]);

  // Client-side filtering for subscribers
  const groupSubscribersFiltered = useMemo(() => {
    const search = groupSubscribersFilter.search;
    if (search) {
      return groupSubscribers.filter(
        (m) =>
          m.MemberName &&
          m.MemberName.toLowerCase().includes(search.toLowerCase())
      );
    }
    return groupSubscribers;
  }, [groupSubscribers, groupSubscribersFilter.search]);

  const invalidateMembers = () => {
    queryClient.invalidateQueries({ queryKey: ["groupMembers", group_id] });
  };

  const saveMutation = useMutation({
    mutationFn: async (member: Record<string, unknown> & { _id: string }) => {
      return await callApi("PUT", `${GROUPMEMBERS_ENDPOINT}/${member._id}`, {
        data: member,
      });
    },
    onSuccess: invalidateMembers,
  });

  const addMutation = useMutation({
    mutationFn: async (contact_id: string) => {
      const member = {
        ContactPersonalId: contact_id,
        GroupId: group_id,
      };
      return await callApi("POST", GROUPMEMBERS_ENDPOINT, {
        data: member,
      });
    },
    onSuccess: invalidateMembers,
  });

  const deleteMutation = useMutation({
    mutationFn: async (_id: string | number) => {
      return await callApi("DELETE", `${GROUPMEMBERS_ENDPOINT}/${_id}`);
    },
    onSuccess: invalidateMembers,
  });

  const saveGroupMember = async (member: Record<string, unknown> & { _id: string }) => {
    return saveMutation.mutateAsync(member);
  };

  const addGroupMember = async (contact_id: string) => {
    return addMutation.mutateAsync(contact_id);
  };

  const deleteGroupMember = async (_id: string | number) => {
    return deleteMutation.mutateAsync(_id);
  };

  const refreshGroupMembers = () => {
    invalidateMembers();
  };

  const groupMembersSaving =
    saveMutation.isPending || addMutation.isPending || deleteMutation.isPending;

  return {
    groupMembers,
    groupSubscribers,
    groupMembersLoaded: !isLoading,
    groupSubscribersLoaded: !isLoading,
    refreshGroupMembers,
    setGroupMembersFilter,
    setGroupSubscribersFilter,
    groupMembersFiltered,
    groupSubscribersFiltered,
    saveGroupMember,
    addGroupMember,
    deleteGroupMember,
    groupMembersSaving,
  };
}
